// The floating panel both pickers open (the owner, 2026-10-30: the browser's own select list and calendar looked nothing
// like the site). One panel at a time, fixed to the viewport and appended to <body>, so no overflow:hidden parent clips it
// and a modal's stacking does not hide it. It opens under its field, or over it when there is more room above, follows
// the field while the page scrolls, and closes on a press outside, Escape (the picker's own key handler) or when the field
// scrolls out of view.

export interface PopupOptions {
    /** At least as wide as the field (a select's list). */
    matchWidth?: boolean;
    /** The tallest the panel may grow; it shrinks further to the room left in the viewport. */
    maxHeight?: number;
    /** Called once, after the panel is gone. */
    onClose?: () => void;
}

export interface Popup {
    readonly panel: HTMLElement;
    readonly anchor: HTMLElement;
    place(): void;
    close(): void;
}

let open: Popup | null = null;

/** The panel on screen now, if any. */
export function currentPopup(): Popup | null {
    return open;
}

export function closePopup(): void {
    if (open) {
        open.close();
    }
}

export function openPopup(doc: Document, anchor: HTMLElement, panel: HTMLElement, options: PopupOptions = {}): Popup {
    closePopup();
    const win = doc.defaultView as Window;
    const gap = 4;
    const margin = 8;
    let closed = false;

    panel.classList.add('ph-pop');
    doc.body.appendChild(panel);

    const place = () => {
        if (closed) {
            return;
        }
        const r = anchor.getBoundingClientRect();
        const vw = doc.documentElement.clientWidth;
        const vh = win.innerHeight;
        // The field left the screen (scrolled away, or removed by its page): nothing to point at any more.
        if (!anchor.isConnected || r.bottom < 0 || r.top > vh || (r.width === 0 && r.height === 0)) {
            popup.close();
            return;
        }
        if (options.matchWidth) {
            panel.style.minWidth = Math.min(Math.max(r.width, 168), vw - margin * 2) + 'px';
        }
        panel.style.maxWidth = (vw - margin * 2) + 'px';
        panel.style.maxHeight = '';
        const below = vh - r.bottom - gap - margin;
        const above = r.top - gap - margin;
        const natural = Math.min(panel.offsetHeight, options.maxHeight || Infinity);
        const down = natural <= below || below >= above;
        const room = down ? below : above;
        if (options.maxHeight) {
            panel.style.maxHeight = Math.max(140, Math.min(room, options.maxHeight)) + 'px';
        }
        const h = panel.offsetHeight;
        const w = panel.offsetWidth;
        const rtl = win.getComputedStyle(anchor).direction === 'rtl';
        let left = rtl ? r.right - w : r.left;
        left = Math.max(margin, Math.min(left, vw - w - margin));
        const top = down ? r.bottom + gap : r.top - gap - h;
        panel.style.left = Math.round(left) + 'px';
        panel.style.top = Math.round(Math.max(margin, Math.min(top, vh - h - margin))) + 'px';
        panel.style.transformOrigin = (down ? 'top ' : 'bottom ') + (rtl ? 'right' : 'left');
        panel.classList.toggle('ph-pop--up', !down);
    };

    const onPointerDown = (e: Event) => {
        const t = e.target as Node;
        if (!panel.contains(t) && !anchor.contains(t)) {
            popup.close();
        }
    };
    const onScroll = (e: Event) => {
        if (e.target instanceof Node && panel.contains(e.target as Node)) {
            return;
        }
        place();
    };
    const onResize = () => place();

    const popup: Popup = {
        panel,
        anchor,
        place,
        close: () => {
            if (closed) {
                return;
            }
            closed = true;
            if (open === popup) {
                open = null;
            }
            doc.removeEventListener('pointerdown', onPointerDown, true);
            win.removeEventListener('scroll', onScroll, true);
            win.removeEventListener('resize', onResize);
            panel.classList.add('ph-pop--leaving');
            win.setTimeout(() => panel.remove(), 110);
            if (options.onClose) {
                options.onClose();
            }
        },
    };
    open = popup;

    place();
    // The opening animation runs from the side nearest the field (transform-origin above).
    win.requestAnimationFrame(() => panel.classList.add('ph-pop--in'));
    doc.addEventListener('pointerdown', onPointerDown, true);
    win.addEventListener('scroll', onScroll, true);
    win.addEventListener('resize', onResize);
    return popup;
}

/** Scrolls a list so the row is inside it, the least distance (no smooth scroll: keys repeat). */
export function keepInView(list: HTMLElement, row: HTMLElement, center = false): void {
    const top = row.offsetTop;
    const bottom = top + row.offsetHeight;
    if (center) {
        list.scrollTop = top - (list.clientHeight - row.offsetHeight) / 2;
    } else if (top < list.scrollTop) {
        list.scrollTop = top - 4;
    } else if (bottom > list.scrollTop + list.clientHeight) {
        list.scrollTop = bottom - list.clientHeight + 4;
    }
}

export function el<K extends keyof HTMLElementTagNameMap>(doc: Document, tag: K, className?: string, text?: string): HTMLElementTagNameMap[K] {
    const e = doc.createElement(tag);
    if (className) {
        e.className = className;
    }
    if (text !== undefined) {
        e.textContent = text;
    }
    return e;
}

/** Writes a value the way a person typing would: Angular's forms read the input event, (change) handlers the change. */
export function commit(field: HTMLInputElement | HTMLSelectElement, write: () => void): void {
    const before = field.value;
    write();
    if (field.value !== before) {
        field.dispatchEvent(new Event('input', { bubbles: true }));
        field.dispatchEvent(new Event('change', { bubbles: true }));
    }
}

/** Phone and tablet presses keep the system's own pickers: the wheels and sheets made for a thumb. */
export function trackPointer(doc: Document): () => boolean {
    let touch = false;
    doc.addEventListener('pointerdown', e => touch = (e as PointerEvent).pointerType === 'touch', true);
    return () => touch;
}

export const ICONS = {
    check: '<svg viewBox="0 0 256 256" aria-hidden="true"><path fill="currentColor" d="M232.49,80.49l-128,128a12,12,0,0,1-17,0l-56-56a12,12,0,1,1,17-17L96,183,215.51,63.51a12,12,0,0,1,17,17Z"/></svg>',
    search: '<svg viewBox="0 0 256 256" aria-hidden="true"><path fill="currentColor" d="M232.49,215.51,185,168a92.12,92.12,0,1,0-17,17l47.53,47.54a12,12,0,0,0,17-17ZM44,112a68,68,0,1,1,68,68A68.07,68.07,0,0,1,44,112Z"/></svg>',
    left: '<svg viewBox="0 0 256 256" aria-hidden="true"><path fill="currentColor" d="M168.49,199.51a12,12,0,0,1-17,17l-80-80a12,12,0,0,1,0-17l80-80a12,12,0,0,1,17,17L97,128Z"/></svg>',
    right: '<svg viewBox="0 0 256 256" aria-hidden="true"><path fill="currentColor" d="M184.49,136.49l-80,80a12,12,0,0,1-17-17L159,128,87.51,56.49a12,12,0,0,1,17-17l80,80A12,12,0,0,1,184.49,136.49Z"/></svg>',
    caret: '<svg viewBox="0 0 256 256" aria-hidden="true"><path fill="currentColor" d="M216.49,104.49l-80,80a12,12,0,0,1-17,0l-80-80a12,12,0,0,1,17-17L128,159l71.51-71.52a12,12,0,0,1,17,17Z"/></svg>',
};
